// One vocabulary for where a job and a visit stand (job-page study §5.5 counted nine for a job and six
// for a visit): the job page's header and strip, the lists, search, the customer record, calls and the
// board all say these words, so the same job never reads "Dispatched" in one place and "On the way" in
// another. The app imports it as @flowology/ui-web/words (no components, no CSS).
import type { MarkState } from '../components/StateMark/StateMark.tsx';

/** A job's status as the server keeps it. */
export type JobStatus = 'open' | 'in_progress' | 'awaiting_review' | 'completed' | 'cancelled';
/** A visit's status as the server keeps it. */
export type VisitStatus = 'scheduled' | 'dispatched' | 'working' | 'paused' | 'closed' | 'cancelled';

/**
 * Where a job stands, as the job page reads it from its status, its visits and its invoice: a job
 * under way whose visit stopped for parts is on hold, and a completed one is done, or done and paid.
 */
export type JobState = 'booked' | 'onhold' | 'underway' | 'review' | 'unpaid' | 'paid' | 'cancelled';

export const JOB_STATE_WORDS: Record<JobState, string> = {
  booked: 'Booked',
  onhold: 'On hold',
  underway: 'Under way',
  review: 'Waiting for review',
  unpaid: 'Done',
  paid: 'Done and paid',
  cancelled: 'Cancelled',
};

/** How each state is drawn beside its word (StateMark). */
export const JOB_STATE_MARK: Record<JobState, MarkState> = {
  booked: 'booked',
  onhold: 'waiting',
  underway: 'working',
  review: 'waiting',
  unpaid: 'done',
  paid: 'done',
  cancelled: 'cancelled',
};

/** A booked job with no visit on the calendar yet. */
export const TO_SCHEDULE = 'To schedule';

const JOB_STATUS_WORDS: Record<JobStatus, string> = {
  open: 'Booked',
  in_progress: 'Under way',
  awaiting_review: 'Waiting for review',
  completed: 'Done',
  cancelled: 'Cancelled',
};

/** "awaiting_review" → "Awaiting review": a word the server sent that this list doesn't know yet. */
const plain = (s: string) => s.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

/**
 * A job's word from its status alone, where its visits and money aren't to hand (a list row, a search
 * hit, a customer's history). An open job with nothing booked is "To schedule" when the caller knows.
 */
export function jobStatusWord(status: string, opts: { unscheduled?: boolean } = {}): string {
  if (status === 'open' && opts.unscheduled) return TO_SCHEDULE;
  return JOB_STATUS_WORDS[status as JobStatus] ?? plain(status);
}

/** How a job's status alone is drawn. */
export function jobStatusMark(status: string): MarkState {
  return status === 'in_progress'
    ? 'working'
    : status === 'awaiting_review'
      ? 'waiting'
      : status === 'completed'
        ? 'done'
        : status === 'cancelled'
          ? 'cancelled'
          : 'booked';
}

/** What jobStateOf reads of a job. */
export interface JobFacts {
  status: string;
  visits: readonly { status: string }[];
  /** The primary invoice's balance, in cents; none (or null) when there's no invoice. */
  balanceCents?: number | null;
}

/** Where a job stands, from its status, its visits and what's owing. */
export function jobStateOf(job: JobFacts): JobState {
  if (job.status === 'cancelled') return 'cancelled';
  if (job.status === 'completed') return (job.balanceCents ?? 0) > 0 ? 'unpaid' : 'paid';
  if (job.status === 'awaiting_review') return 'review';
  const live = job.visits.filter((v) => v.status !== 'cancelled');
  if (live.some((v) => v.status === 'dispatched' || v.status === 'working')) return 'underway';
  // stopped (for parts, a return visit) with nothing booked after it
  if (live.some((v) => v.status === 'paused') && !live.some((v) => v.status === 'scheduled')) return 'onhold';
  return 'booked';
}

export const VISIT_WORDS: Record<VisitStatus, string> = {
  scheduled: 'Booked',
  dispatched: 'On the way',
  working: 'On site',
  paused: 'Paused',
  closed: 'Done',
  cancelled: 'Cancelled',
};

/** A visit's word; one sent that nobody has set off on yet says so (QA-687). */
export function visitWord(status: string, opts: { notStarted?: boolean } = {}): string {
  if (status === 'dispatched' && opts.notStarted) return 'Sent, not started';
  return VISIT_WORDS[status as VisitStatus] ?? plain(status);
}

/** How a visit's status is drawn beside its word. */
export function visitMark(status: string): MarkState {
  return status === 'dispatched' || status === 'working'
    ? 'working'
    : status === 'paused'
      ? 'waiting'
      : status === 'closed'
        ? 'done'
        : status === 'cancelled'
          ? 'cancelled'
          : 'booked';
}

/** A part ordered for a job, its quantities already in words ("2", "0.5"); none is null. */
export interface PartLine {
  received: string;
  ordered: string;
  /** Called off with the supplier: a credit, not a delivery. */
  cancelled: string | null;
  /** Ordered, less what came in and what was cancelled. */
  expected: string | null;
  /** The supplier says the rest is back-ordered. */
  backOrdered: boolean;
}

/**
 * Where a part on order stands, in the job page's and the lists' one wording: "1 of 2 in", "0 of 3 in,
 * 2 cancelled, 1 still expected", "1 of 2 in, the rest back-ordered".
 */
export function partLineWords(l: PartLine): string {
  const bits = [`${l.received} of ${l.ordered} in`];
  if (l.cancelled) bits.push(`${l.cancelled} cancelled`);
  if (l.expected) bits.push(l.backOrdered ? 'the rest back-ordered' : `${l.expected} still expected`);
  return bits.join(', ');
}
